// import AgTable from '@navi/web-ui/lib/components/AgTable/AgTable';
import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import Tag from '@navi/web-ui/lib/primitives/Tag/Tag';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import { ICellRendererParams } from 'ag-grid-community';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { QueryParams } from 'src/components/interfaces';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import TableLoader from 'src/components/TableLoader/TableLoader';
import { CasesDetailsFeedbackTypesCodeMap } from 'src/pages/Cases/constants/CasesConstants';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { formatDateTime, isObjectEmpty } from 'src/utils/commonUtils';
import { readQueryParams, createQueryParams } from 'src/utils/QueryParamsHelper';
import { fetchFeedbackHistory } from '../../actions/overviewActions';
import {
  FEEDBACK_HISTORY_TABLE,
  NO_FEEDBACK_HISTORY_TEXT
} from '../../constants/Overview.constant';
import { createPayloadForFeedbackHistory } from '../../feedbackForm/utlis';
import styles from './index.module.scss';
import cx from 'classnames';
import { dateFormat, DateFormat, formatDate, utcToIST } from 'src/utils/DateHelper';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import { User } from 'src/reducers/commonSlice';
import { overviewFeedbackStyles } from './utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import EllipsisText from '@cp/src/components/EllipsisText';
import Switch from '@cp/src/components/Switch/Switch';
import DateTime from '../DateTime';

const AgTable = React.lazy(
  () => import(/* webpackChunkName: ['table'] */ '@navi/web-ui/lib/components/AgTable/AgTable')
);

export const getColumnDefs = (user: User) => {
  return [
    {
      field: 'feedbackTime',
      headerName: 'Date & Time',
      cellRenderer: (params: ICellRendererParams) => {
        const istDate = utcToIST(
          params.data.ameyoNonConnectedInteractionsInfo?.length
            ? params.data.ameyoNonConnectedInteractionsInfo[0].createdAt
            : params.value
        );
        return <DateTime istDate={istDate} />;
      }
    },
    {
      field: 'agentName',
      headerName: 'Agent Name',
      cellRenderer: (params: ICellRendererParams) => {
        const value = params.value;
        return (
          <Tooltip placement="top" hiddenPadding={0} hideStrategy="referenceHidden">
            <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
              <EllipsisText text={value} />
            </TooltipTrigger>
            <TooltipContent className="tooltipWrapper">{value}</TooltipContent>
          </Tooltip>
        );
      }
    },
    { field: 'phoneNumber', headerName: 'Number' },
    {
      field: 'feedback',
      headerName: 'Feedback',
      cellRenderer: (params: ICellRendererParams) => {
        const { feedback, hierarchyTag, interactionLevel } = params?.data || {};
        const { color = 'orange', label = feedback } =
          CasesDetailsFeedbackTypesCodeMap[feedback] || {};
        return user?.revampedFeedbackViewAllowed ? (
          <>
            <Tag
              color={overviewFeedbackStyles(interactionLevel)}
              label={label}
              variant="transparent"
            />
            <div className={styles.dispositionHierarchyTag}>{hierarchyTag}</div>
          </>
        ) : (
          <Tag color={color} label={label} variant="transparent" />
        );
      }
    },
    {
      field: 'comments',
      headerName: 'Comments',
      cellRenderer: (params: ICellRendererParams) => {
        const value = params.value;
        return (
          <Tooltip placement="top" hiddenPadding={0} hideStrategy="referenceHidden">
            <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
              <EllipsisText text={value} />
            </TooltipTrigger>
            <TooltipContent className="tooltipWrapper">{value}</TooltipContent>
          </Tooltip>
        );
      }
    }
  ];
};

const FeedbackHistory = () => {
  const {
    loanId = '',
    customerId = '',
    caseReferenceId = '',
    caseBusinessVertical = ''
  } = useParams();
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );
  const user = useSelector((state: RootState) => state.common.userData);
  const { data: feedbackHistoryData, loading: feedbackHistoryLoading } =
    pageData?.feedbackHistory || {};
  const [searchParams] = useSearchParams();
  const { [FEEDBACK_HISTORY_TABLE]: queryParams, ...rest } = readQueryParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getFeedbackHistoryData = () => {
    const payload = createPayloadForFeedbackHistory(loanId, caseReferenceId, caseBusinessVertical);
    dispatch(fetchFeedbackHistory(loanId, customerId, payload));
  };

  useEffect(() => {
    if (!isObjectEmpty(queryParams)) {
      getFeedbackHistoryData();
    }
  }, [searchParams]);

  const updateQueryParamsAndNavigate = (params?: QueryParams, lastUsed?: string) => {
    const url = createQueryParams({
      [FEEDBACK_HISTORY_TABLE]: { ...queryParams, ...params, lastUsed },
      ...rest
    });
    navigate(url, {
      replace: true
    });
  };

  useEffect(() => {
    updateQueryParamsAndNavigate({ contactable: 'true', page: 0 });
  }, [loanId]);

  const getCurrentPageNumber = (): number => {
    return Number(queryParams?.page) + 1 || 1;
  };

  const getCurrentPageSize = (): number => {
    return Number(queryParams?.size) || 10;
  };

  // Query params handlers on different events
  const handlePageSizeChange = (size: number) =>
    updateQueryParamsAndNavigate({ size: size.toString(), page: 0 });

  const handlePageChange = (page: number) =>
    updateQueryParamsAndNavigate({ page: (page - 1).toString() });

  const handleContactableSwitchChange = () => {
    const isChecked = queryParams?.contactable === 'true';
    updateQueryParamsAndNavigate({ contactable: (!isChecked).toString(), page: 0 });
  };

  //TODO: use this when switch is fixed
  // const handleContactableSwitchChange = (e: ChangeEvent<HTMLInputElement | HTMLSpanElement>) =>
  //   updateQueryParamsAndNavigate({ contactable: e.target.checked.toString(), page: 0 });
  return (
    <div
      className={cx(styles.feedbackHistoryWrapper, {
        [styles.feedbackPositionConditional]: feedbackHistoryData?.totalElements
      })}
    >
      <div className={styles.header}>
        <Typography variant="h4" className={styles.header__text}>
          Communication History
        </Typography>
        <Switch
          checked={queryParams?.contactable === 'true'}
          onChange={handleContactableSwitchChange}
          headerText="Only contactable"
        />
      </div>
      <div className={styles.table}>
        <SuspenseLoader
          loading={!!feedbackHistoryLoading}
          fallBack={<TableLoader numberOfLines={6} />}
        >
          <AgTable
            defaultColDef={{
              suppressMovable: true,
              wrapText: true,
              autoHeight: true
            }}
            gridOptions={{ suppressCellSelection: true }}
            rowData={feedbackHistoryData?.content}
            columnDefs={getColumnDefs(user)}
            sizeColumnsToFit
            rowClassRules={{ 'rr-block': () => true }}
            theme={'alpine'}
            domLayout="autoHeight"
            noRowsOverlayComponent={() => (
              <Typography variant="p4" className={styles.emptyListText}>
                {NO_FEEDBACK_HISTORY_TEXT}
              </Typography>
            )}
            paginationWrapperClasses={'paginationWrapperClasses'}
            PaginationComponent={
              <Pagination
                totalCount={feedbackHistoryData?.totalElements || 0}
                onPageChange={handlePageChange}
                currentPage={getCurrentPageNumber()}
                onPageSizeChange={handlePageSizeChange}
                pageSize={getCurrentPageSize()}
                pageNumberDropDownPosition={DropDownPosition.TOP}
              />
            }
          />
        </SuspenseLoader>
      </div>
    </div>
  );
};

export default FeedbackHistory;
